import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Settings as SettingsIcon, LogOut, Sprout, Sun, Moon, Lock, ShieldCheck,
} from "lucide-react";
import { NAV_ALL as NAV } from "@/config/nav.config";
import SidebarStatusBadge from "@/layout/SidebarStatusBadge";

const ADMIN_QUICKNAV = [
  { to: "/admin/metrics", label: "Metrics", testId: "sidebar-admin-metrics" },
  { to: "/admin/users", label: "Users", testId: "sidebar-admin-users" },
  { to: "/admin/waitlist", label: "Waitlist", testId: "sidebar-admin-waitlist" },
  { to: "/admin/audit", label: "Audit", testId: "sidebar-admin-audit" },
  { to: "/admin/trials", label: "Trials", testId: "sidebar-admin-trials" },
];

/**
 * DesktopSidebar — full nav rail visible on `lg` and above.
 *
 * Owns: brand header, NAV items (with Pro-gate lock), status badge
 * (delegated to <SidebarStatusBadge/>), admin quick-nav footer,
 * settings + theme + user profile + logout controls.
 */
export default function DesktopSidebar({
  user,
  isAdmin,
  isPro,
  hasFullAccess,
  billingStatus,
  trialStatus,
  daysUntilExpiry,
  theme,
  onToggleTheme,
  onLogout,
  onProLocked,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 border-r border-[var(--border-default)] bg-[var(--bg-primary)]">
      <div className="px-5 py-5 border-b border-[var(--border-default)] flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center">
          <Sprout className="text-white" size={17} />
        </div>
        <div>
          <div className="font-mono text-[9px] tracking-[0.3em] text-[var(--text-secondary)] uppercase">Personal</div>
          <div className="font-medium text-[15px] tracking-tight leading-none" style={{ fontFamily: "var(--font-heading)" }}>Finance Optimizer</div>
        </div>
      </div>

      <nav className="p-3 flex-1 overflow-y-auto">
        {NAV.map(({ to, i18nKey, icon: Icon, testId, proOnly }) => {
          const gated = proOnly && !hasFullAccess;
          if (gated) {
            return (
              <button
                key={to}
                data-testid={testId}
                onClick={() => onProLocked(i18nKey)}
                className="sidebar-link w-full text-left opacity-60 hover:opacity-100 cursor-pointer group"
                title={t("upgrade.pro_feature_locked", { defaultValue: "Pro only — upgrade to unlock." })}
              >
                <Icon size={17} strokeWidth={2} />
                <span className="flex-1">{t(`nav.${i18nKey}`)}</span>
                <Lock size={12} className="text-[var(--text-muted)] group-hover:text-[var(--accent-primary)]" />
              </button>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <Icon size={17} strokeWidth={2} />
              <span>{t(`nav.${i18nKey}`)}</span>
            </NavLink>
          );
        })}

        <SidebarStatusBadge
          isAdmin={isAdmin}
          isPro={isPro}
          billingStatus={billingStatus}
          trialStatus={trialStatus}
          daysUntilExpiry={daysUntilExpiry}
        />
      </nav>

      <div className="p-3 border-t border-[var(--border-default)]">
        {isAdmin && (
          <div className="mb-3" data-testid="sidebar-admin-quicknav">
            <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
              <ShieldCheck size={11} /> Admin console
            </div>
            <div className="flex flex-wrap gap-1 px-1">
              {ADMIN_QUICKNAV.map((a) => (
                <button
                  key={a.to}
                  onClick={() => navigate(a.to)}
                  data-testid={a.testId}
                  className="text-[11px] px-2 py-1 rounded-md border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[#E09E50] hover:border-[#E09E50] transition-colors"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button className="sidebar-link flex-1" onClick={() => navigate("/settings")} data-testid="nav-settings">
            <SettingsIcon size={17} /> <span>{t("nav.settings")}</span>
          </button>
          <button
            onClick={onToggleTheme}
            data-testid="nav-theme-toggle"
            title={theme === "dark" ? "Switch to Daylight" : "Switch to Moonlit"}
            aria-label="Toggle theme"
            className="h-9 w-9 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors flex items-center justify-center"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
        <div className="flex items-center gap-3 px-3 py-3 mt-1">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-[var(--border-default)]" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-[var(--bg-accent)] flex items-center justify-center text-[var(--accent-primary)] font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              {isAdmin && (
                <span
                  className="text-[9px] font-semibold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded"
                  style={{ background: "#E09E50", color: "white" }}
                  data-testid="sidebar-user-admin-chip"
                >
                  Admin
                </span>
              )}
            </div>
            <div className="text-[11px] text-[var(--text-muted)] truncate">{user?.email}</div>
          </div>
          <button onClick={onLogout} className="btn-danger-ghost" data-testid="nav-logout" title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
